import { Component } from '@theme/component';
import {
  debounce,
  isClickedOutside,
  lockScroll,
  onAnimationEnd,
  unlockScroll,
} from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  #previousScrollY = 0;
  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    /*
     * Apply Android Back handling only to the Size Guide.
     * This prevents Cart Drawer and other dialogs from being affected.
     */
    if (this.#isSizeGuide) {
      dialog.addEventListener('cancel', this.#handleSizeGuideCancel);
      dialog.addEventListener('close', this.#handleSizeGuideNativeClose);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { dialog } = this.refs;

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    if (this.#isSizeGuide) {
      dialog?.removeEventListener('cancel', this.#handleSizeGuideCancel);
      dialog?.removeEventListener('close', this.#handleSizeGuideNativeClose);
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    unlockScroll(dialog);
  }

  /**
   * Returns true only for the Size Guide dialog component.
   */
  get #isSizeGuide() {
    return this.classList.contains('popup-link--size-chart');
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;

    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Android system Back triggers the native dialog cancel event.
   * Only the Size Guide uses this custom behavior.
   *
   * @param {Event} event
   */
  #handleSizeGuideCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Safety cleanup if the mobile browser closes the Size Guide natively.
   */
  #handleSizeGuideNativeClose = () => {
    const { dialog } = this.refs;

    unlockScroll(dialog);

    dialog.classList.remove('dialog-closing');
    dialog.style.removeProperty('animation');

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    this.#isClosing = false;
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#isClosing) return;

    this.#previousScrollY = getScrollTop();

    requestAnimationFrame(() => {
      if (
        !this.isConnected ||
        document.visibilityState === 'hidden' ||
        dialog.open
      ) {
        return;
      }

      lockScroll(dialog);
      dialog.showModal();

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (this.#isClosing) return;

    /*
     * On some Android browsers, the native dialog may already be closed
     * when the cancel handling reaches this method.
     */
    if (!dialog.open) {
      unlockScroll(dialog);
      this.#isClosing = false;
      return;
    }

    this.#isClosing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.style.animation = 'none';

    /*
     * Force a reflow so the closing animation restarts correctly.
     */
    void dialog.offsetWidth;

    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    try {
      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });
    } finally {
      /*
       * Release only this dialog's scroll lock.
       * Do not remove the global scroll-lock attribute manually,
       * because another drawer may still own a lock.
       */
      unlockScroll(dialog);

      if (document.visibilityState !== 'hidden') {
        scrollTo({
          top: this.#previousScrollY,
          behavior: 'instant',
        });
      }

      if (dialog.open) {
        dialog.close();
      }

      dialog.classList.remove('dialog-closing');
      dialog.style.removeProperty('animation');

      this.#isClosing = false;

      this.dispatchEvent(new DialogCloseEvent());
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when clicking outside.
   *
   * @param {MouseEvent} event
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Handles the Escape key.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum dialog width.
   *
   * @returns {number}
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum dialog width.
   *
   * @returns {number}
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    if (!event.target.hasAttribute('scroll-lock')) return;

    const { open } = event.target;

    if (open) {
      lockScroll(event.target);
    } else {
      unlockScroll(event.target);
    }
  },
  { capture: true }
);